/**
 * Phase 4 — what an automated probe *means* (§9.3, design brief §4.1b).
 *
 * The rule that shapes everything here: **a probe proposes, a human decides.** The portal
 * never writes an item state from a GitLab reading. It offers an answer with a deep link
 * as evidence, and an auditor accepts it or does not.
 *
 * That is not timidity. A probe reads configuration; an item asks whether a practice is
 * in place. "Protected branches are configured" is evidence for A-03, not proof of it —
 * and an auditor who found a reason to reject an item that a probe likes must be able to
 * keep that answer without the machine overwriting them overnight.
 */

export type ProbeStatus = 'PASS' | 'FAIL' | 'INDETERMINATE';

/** The subset of item states a probe can have an opinion about. */
type ProbeableState = 'PENDING' | 'PARTIAL' | 'VERIFIED' | 'REJECTED' | 'NOT_APPLICABLE' | 'WAIVED';

export interface ProbeSpec {
  readonly probeId: string;
  readonly itemRef: string;
  /** What the probe reads, in the words the integrations screen shows. */
  readonly reads: string;
  /** Per repository, or once for the project. */
  readonly scope: 'REPOSITORY' | 'PROJECT';
  /** A-01 and A-26 are asked of the primary repository only (§3.4). */
  readonly primaryRepoOnly: boolean;
}

/**
 * The sixteen probes, matching `autoCheck` in the checklist metadata exactly.
 *
 * Kept here rather than derived from the template so the integrations screen can list
 * them before any project exists, and so a probe with no implementation is a type error
 * rather than a silent absence.
 */
export const PROBES: readonly ProbeSpec[] = [
  { probeId: 'gitlab.repo-exists', itemRef: 'A-01', reads: 'Repository exists and is not archived', scope: 'REPOSITORY', primaryRepoOnly: true },
  { probeId: 'gitlab.default-branch-documented', itemRef: 'A-02', reads: 'CONTRIBUTING.md or docs/branching.md present', scope: 'REPOSITORY', primaryRepoOnly: false },
  { probeId: 'gitlab.protected-branches', itemRef: 'A-03', reads: 'Default branch protected, no direct push', scope: 'REPOSITORY', primaryRepoOnly: false },
  { probeId: 'gitlab.merge-request-approvals', itemRef: 'A-04', reads: 'Merge requests need at least one approval', scope: 'REPOSITORY', primaryRepoOnly: false },
  { probeId: 'gitlab.commit-signing', itemRef: 'A-06', reads: 'Signed-commit policy enabled', scope: 'REPOSITORY', primaryRepoOnly: false },
  { probeId: 'gitlab.architecture-doc', itemRef: 'A-16', reads: 'A diagram or architecture file under docs/', scope: 'REPOSITORY', primaryRepoOnly: true },
  { probeId: 'gitlab.readme-runbook', itemRef: 'A-17', reads: 'README links a runbook or operating guide', scope: 'REPOSITORY', primaryRepoOnly: true },
  { probeId: 'gitlab.ci-config-versioned', itemRef: 'A-26', reads: '.gitlab-ci.yml is committed', scope: 'REPOSITORY', primaryRepoOnly: true },
  { probeId: 'gitlab.pipeline-on-merge-request', itemRef: 'B-12', reads: 'A pipeline runs on merge requests', scope: 'REPOSITORY', primaryRepoOnly: false },
  { probeId: 'gitlab.status-check-blocking', itemRef: 'B-15', reads: 'Merge is blocked while the pipeline fails', scope: 'REPOSITORY', primaryRepoOnly: false },
  { probeId: 'gitlab.dependency-review-cadence', itemRef: 'B-27', reads: 'A dependency-update job or bot is configured', scope: 'REPOSITORY', primaryRepoOnly: false },
  { probeId: 'gitlab.scheduled-pipeline', itemRef: 'B-31', reads: 'At least one active scheduled pipeline', scope: 'REPOSITORY', primaryRepoOnly: false },
  { probeId: 'gitlab.runbook-present', itemRef: 'D-17', reads: 'An incident runbook file exists', scope: 'REPOSITORY', primaryRepoOnly: true },
  { probeId: 'gitlab.audit-evidence-folder', itemRef: 'E-02', reads: 'An evidence folder exists in the repository', scope: 'REPOSITORY', primaryRepoOnly: true },
  { probeId: 'gitlab.deprecation-policy', itemRef: 'F-20', reads: 'A deprecation policy document exists', scope: 'REPOSITORY', primaryRepoOnly: true },
  { probeId: 'gitlab.eol-register', itemRef: 'F-21', reads: 'An end-of-life or support register exists', scope: 'REPOSITORY', primaryRepoOnly: false },
];

const BY_ID = new Map(PROBES.map((probe) => [probe.probeId, probe]));

export function probeSpec(probeId: string): ProbeSpec | undefined {
  return BY_ID.get(probeId);
}

/** The probes that apply to one item ref. */
export function probesForItem(itemRef: string): readonly ProbeSpec[] {
  return PROBES.filter((probe) => probe.itemRef === itemRef);
}

/**
 * The state a probe result proposes, or null when it proposes nothing.
 *
 * `INDETERMINATE` proposes nothing on purpose: "the API did not tell us enough to judge"
 * must never collapse into "it fails" (§9.3). A probe that cannot reach GitLab has not
 * discovered a gap, it has discovered a network problem.
 */
export function proposedState(status: ProbeStatus): 'VERIFIED' | 'REJECTED' | null {
  if (status === 'PASS') return 'VERIFIED';
  if (status === 'FAIL') return 'REJECTED';
  return null;
}

export type DivergenceKind =
  /** The probe passes but a human rejected it. */
  | 'PROBE_PASSES_HUMAN_REJECTED'
  /** The probe fails but a human verified it. */
  | 'PROBE_FAILS_HUMAN_VERIFIED'
  /** The probe fails and the item is waived — worth surfacing, not a contradiction. */
  | 'PROBE_FAILS_ITEM_WAIVED';

export interface Divergence {
  readonly kind: DivergenceKind;
  /** Plain words for the resolution screen. */
  readonly detail: string;
}

/**
 * Does this probe result contradict the recorded answer?
 *
 * The distinction that matters most: **`PENDING` is not a divergence.** An unanswered item
 * that a probe likes is an *opportunity* — an answer waiting to be accepted — and mixing
 * the two would bury real disagreements under two hundred unanswered items on the first
 * night the runner ever ran.
 *
 * `NOT_APPLICABLE` is never a divergence either: applicability is a property of the
 * project, decided by the context profile, and no repository reading overrides it.
 */
export function divergenceFor(input: {
  readonly status: ProbeStatus;
  readonly state: ProbeableState;
}): Divergence | null {
  if (input.status === 'INDETERMINATE') return null;
  if (input.state === 'PENDING' || input.state === 'NOT_APPLICABLE') return null;

  if (input.status === 'PASS' && input.state === 'REJECTED') {
    return {
      kind: 'PROBE_PASSES_HUMAN_REJECTED',
      detail:
        'GitLab now satisfies this check, but the item is recorded as Rejected. Either the ' +
        'configuration was fixed since the audit, or the rejection was for a reason the probe ' +
        'cannot see.',
    };
  }

  if (input.status === 'FAIL' && input.state === 'VERIFIED') {
    return {
      kind: 'PROBE_FAILS_HUMAN_VERIFIED',
      detail:
        'GitLab no longer satisfies this check, but the item is recorded as Verified. Either ' +
        'the configuration regressed, or the verification rested on evidence outside GitLab.',
    };
  }

  if (input.status === 'FAIL' && input.state === 'WAIVED') {
    return {
      kind: 'PROBE_FAILS_ITEM_WAIVED',
      detail:
        'The check still fails and the item is waived. That is consistent — but the waiver ' +
        'should be reviewed before it is renewed.',
    };
  }

  return null;
}

/**
 * An unanswered item a probe can answer. Offered, never applied.
 *
 * Separate from `divergenceFor` because these two lists go to different places: an
 * opportunity belongs in the auditor's workspace as a pre-filled suggestion, a divergence
 * belongs in a queue somebody has to work through.
 */
export function isOpportunity(input: {
  readonly status: ProbeStatus;
  readonly state: ProbeableState;
}): boolean {
  return input.state === 'PENDING' && proposedState(input.status) !== null;
}

/**
 * Whether a probe result is fresh enough to act on.
 *
 * A reading from last month is not evidence about today. The runner is nightly, so
 * anything older than two days means the runner has been failing and the screen should
 * say so rather than presenting stale readings as current.
 */
export const PROBE_STALE_AFTER_HOURS = 48;

export function isProbeStale(runAt: Date, now: Date): boolean {
  return now.getTime() - runAt.getTime() > PROBE_STALE_AFTER_HOURS * 3_600_000;
}
